import React from 'react';
import { format, formatDistanceToNow, parseISO } from 'date-fns';

export const CARD = 'bg-white rounded-xl border border-gray-100 shadow-sm';
export const INPUT =
  'rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-persian-500 focus:outline-none focus:ring-1 focus:ring-persian-500';

// Known components in display order. Others the server adds later (e.g.
// attendance) still show, by their own name.
export const COMPONENTS = ['quizzes', 'assignments', 'content', 'engagement'];
const LABELS = {
  quizzes: 'Quizzes',
  assignments: 'Assignments',
  content: 'Content',
  engagement: 'Engagement',
  attendance: 'Attendance',
};
export const componentLabel = (key) =>
  LABELS[key] || key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');

export const componentKeys = (components) => [
  ...COMPONENTS.filter((key) => components && key in components),
  ...Object.keys(components ?? {}).filter((key) => !COMPONENTS.includes(key)),
];

export const score = (value) =>
  value === null || value === undefined ? '–' : Number(value).toFixed(1);

export const percent = (value) =>
  value === null || value === undefined ? '–' : `${Math.round(value)}%`;

export const updatedText = (value) =>
  value
    ? `Updated ${formatDistanceToNow(parseISO(value), { addSuffix: true })}`
    : '';

export const formatDay = (value) => {
  if (!value) return 'Never';
  try {
    return format(parseISO(value), 'd MMM yyyy');
  } catch {
    return value;
  }
};

const barColour = (value) => {
  if (value >= 70) return 'bg-green-500';
  if (value >= 40) return 'bg-amber-400';
  return 'bg-red-400';
};

// One thin bar per component, 0–100, with the value in the tooltip.
export function ComponentBars({ components }) {
  const keys = componentKeys(components);
  if (!keys.length) return <span className="text-xs text-gray-400">–</span>;
  return (
    <div className="flex items-end gap-1" aria-hidden="true">
      {keys.map((key) => {
        const value = components[key];
        return (
          <div
            key={key}
            title={`${componentLabel(key)}: ${percent(value)}`}
            className="flex w-8 flex-col items-center gap-0.5"
          >
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
              <div
                className={`h-full ${barColour(value ?? 0)}`}
                style={{ width: `${Math.max(0, Math.min(100, value ?? 0))}%` }}
              />
            </div>
            <span className="text-[9px] uppercase text-gray-400">
              {componentLabel(key).slice(0, 1)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// A full breakdown: label, bar, value, and what it's made of.
export function Breakdown({ components, details, weights }) {
  const explain = {
    quizzes: () =>
      details &&
      `${details.quizzes_taken ?? 0} of ${
        details.quizzes_total ?? 0
      } quizzes taken${
        details.quiz_first_attempt_avg !== null &&
        details.quiz_first_attempt_avg !== undefined
          ? `, first-attempt average ${percent(details.quiz_first_attempt_avg)}`
          : ''
      }`,
    assignments: () =>
      details &&
      `${details.assignments_submitted ?? 0} of ${
        details.assignments_total ?? 0
      } submitted, ${details.assignments_graded ?? 0} graded${
        details.assignment_avg_grade !== null &&
        details.assignment_avg_grade !== undefined
          ? `, average grade ${percent(details.assignment_avg_grade)}`
          : ''
      }`,
    content: () =>
      details &&
      `${details.weeks_done ?? 0} of ${
        details.weeks_total ?? 0
      } weeks completed`,
    engagement: () =>
      details &&
      `Active ${
        details.active_days_30 ?? 0
      } of the last 30 days; last login ${formatDay(details.last_login)}`,
  };
  return (
    <ul className="m-0 list-none space-y-2 p-0">
      {componentKeys(components).map((key) => (
        <li key={key} className="space-y-0.5">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-700">
              {componentLabel(key)}
              {weights?.[key] !== undefined && (
                <span className="ml-1 text-xs text-gray-400">
                  (weight {weights[key]})
                </span>
              )}
            </span>
            <span className="font-medium text-gray-900">
              {percent(components[key])}
            </span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
            <div
              className={`h-full ${barColour(components[key] ?? 0)}`}
              style={{
                width: `${Math.max(0, Math.min(100, components[key] ?? 0))}%`,
              }}
            />
          </div>
          {explain[key]?.() && (
            <p className="text-xs text-gray-500">{explain[key]()}</p>
          )}
        </li>
      ))}
    </ul>
  );
}

export function KindBadge({ kind }) {
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
        kind === 'specialization'
          ? 'bg-purple-50 text-purple-700'
          : 'bg-persian-500/10 text-persian-600'
      }`}
    >
      {kind === 'specialization' ? 'Specialization' : 'Curriculum'}
    </span>
  );
}
